import React, { useMemo, useState } from "react";
import { ChevronDown, Search, Check } from "lucide-react";
import { COUNTRIES, DEFAULT_COUNTRY, splitPhone } from "@/lib/countries";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Phone input with a country-code dropdown to the left.
 *
 * Props:
 *   value        — the raw E.164 value (no `+`), e.g. "255754510366"
 *   onChange(v)  — emitted whenever country or national number changes
 *   defaultCountryIso — starting country if `value` is empty (default "TZ")
 *   testId       — prefix for data-testids on the two sub-controls
 */
export default function CountryPhoneInput({
  value = "",
  onChange,
  defaultCountryIso = "TZ",
  testId = "phone",
  autoFocus = false,
  placeholder = "7 5 4 5 1 0 3 6 6",
}) {
  const initial = useMemo(() => {
    if (value) return splitPhone(value);
    return { country: COUNTRIES.find((c) => c.iso === defaultCountryIso) || DEFAULT_COUNTRY, national: "" };
  }, [value, defaultCountryIso]);

  const [country, setCountry] = useState(initial.country);
  const [national, setNational] = useState(initial.national);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const emit = (c, n) => {
    const cleaned = String(n || "").replace(/\D/g, "");
    onChange && onChange(cleaned ? `${c.dial}${cleaned}` : "");
  };

  const priority = COUNTRIES.filter((c) => c.group === "priority");
  const africa   = COUNTRIES.filter((c) => c.group === "africa");
  const world    = COUNTRIES.filter((c) => c.group === "world");

  const filter = (list) => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.iso.toLowerCase().includes(q),
    );
  };

  const CountryRow = ({ c }) => (
    <button
      type="button"
      onClick={() => { setCountry(c); setOpen(false); setQuery(""); emit(c, national); }}
      data-testid={`${testId}-country-option-${c.iso}`}
      className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-100 text-left"
    >
      <span className="text-lg leading-none">{c.flag}</span>
      <span className="flex-1 truncate">{c.name}</span>
      <span className="text-slate-500 tabular-nums">+{c.dial}</span>
      {c.iso === country.iso && <Check size={14} className="text-brand-600" />}
    </button>
  );

  return (
    <div className="flex items-stretch gap-0 border border-slate-300 rounded-sm bg-white focus-within:ring-2 focus-within:ring-brand-500/30 focus-within:border-brand-500">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testId}-country-trigger`}
            className="flex items-center gap-2 pl-3 pr-2 border-r border-slate-200 hover:bg-slate-50 rounded-l-sm"
          >
            <span className="text-lg leading-none">{country.flag}</span>
            <span className="text-sm text-slate-700 tabular-nums">+{country.dial}</span>
            <ChevronDown size={14} className="text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[320px] p-0 max-h-[380px] overflow-hidden flex flex-col" data-testid={`${testId}-country-popover`}>
          <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid={`${testId}-country-search`}
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or +code…"
                className="w-full pl-7 pr-2 py-1.5 text-sm rounded-sm border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filter(priority).length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">East Africa</div>
                {filter(priority).map((c) => <CountryRow key={c.iso} c={c} />)}
              </>
            )}
            {filter(africa).length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Africa</div>
                {filter(africa).map((c) => <CountryRow key={c.iso} c={c} />)}
              </>
            )}
            {filter(world).length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Rest of world</div>
                {filter(world).map((c) => <CountryRow key={c.iso} c={c} />)}
              </>
            )}
            {filter(priority).length + filter(africa).length + filter(world).length === 0 && (
              <div className="text-center text-sm text-slate-400 py-6">No matches</div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <Input
        data-testid={`${testId}-national-input`}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        autoFocus={autoFocus}
        value={national}
        placeholder={placeholder}
        onChange={(e) => {
          const v = e.target.value.replace(/[^\d]/g, "");
          setNational(v); emit(country, v);
        }}
        className="border-0 rounded-none rounded-r-sm focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none h-10"
      />
    </div>
  );
}
